import { memo } from "react"
import { CreateUpdateBookingFormValues } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { ConfirmBookingDialog } from "../../../dialogs/createUpdateBookingDialog/ConfirmBookingDialog"
import { ResourceItem, SelectedMainUserState } from "../../createOrderTypes"
import { generateBookingInitialValues } from "../form/formLibBooking"
import { TimelineCalendarEvent } from "./calendar/fullCalendarTypes"
import { BookingConfig, DynamicConfigKey, ExtendedDynamicConfigKey } from "../form/formTypes"
import { InitialValuesState } from "./ResourceBookingDraftContext"

function transformToBookingFormValues(
    values: Record<ExtendedDynamicConfigKey | string, any>,
    user: SelectedMainUserState,
): CreateUpdateBookingFormValues {
    return {
        organizers: values[DynamicConfigKey.MEETING_RESPONSIBLE] ?? { displayName: user.displayName, id: user.userId },
        meetingDetails: {
            meetingName: values[DynamicConfigKey.MEETING_NAME] ?? "",
            isPrivate: values[DynamicConfigKey.PRIVATE_MEETING] ?? true,
            isExternal: values[DynamicConfigKey.EXTERNAL_MEETING] ?? false,
            startDateTime: values["startDateTime"] ?? "",
            endDateTime: values["endDateTime"] ?? "",
            resourceIds: values["resourceIds"]?.map((id: number | string) => Number(id)) ?? [],
        },
        resources: values["resources"] ?? [],
        attendees: values[DynamicConfigKey.ADD_PARTICIPANTS_FORM] ?? [],
        messageToServiceCenter: values[DynamicConfigKey.SERVICE_CENTER_MESSAGE] ?? "",
    }
}

type CreateUpdateBookingDialogWrapProps = Readonly<{
    open: boolean
    onClose: (formValues?: CreateUpdateBookingFormValues) => void
    bookingConfig: BookingConfig
    event: TimelineCalendarEvent
    initialValues: InitialValuesState | null
    handleSubmit: (values: CreateUpdateBookingFormValues) => void
    resourcesList: ResourceItem[]
    handleClearResourceIds: () => void
    user: SelectedMainUserState
    orderId?: number | null
}>

export const CreateUpdateBookingDialogWrap = memo(
    ({
        open,
        onClose,
        event,
        initialValues,
        handleSubmit,
        resourcesList,
        handleClearResourceIds,
        user,
        orderId,
        bookingConfig,
    }: CreateUpdateBookingDialogWrapProps) => {
        const defaultInitialValues = generateBookingInitialValues(
            bookingConfig,
            { startDateTime: event.start, endDateTime: event.end, resourceIds: event.resourceIds },
            initialValues,
            user,
        )

        const onSubmit = (values: Record<ExtendedDynamicConfigKey | string, any>) => {
            handleSubmit(transformToBookingFormValues(values, user))
        }
        const onCloseWithParams = (formValues?: Record<ExtendedDynamicConfigKey | string, any>) => {
            if (formValues) {
                onClose(transformToBookingFormValues(formValues, user))
            } else {
                onClose()
            }
        }

        return (
            <ConfirmBookingDialog
                open={open}
                onClose={onCloseWithParams}
                config={bookingConfig}
                initialValues={defaultInitialValues}
                resourcesList={resourcesList}
                handleSubmit={onSubmit}
                timeRange={event.timeRange}
                handleClearResourceIds={handleClearResourceIds}
                user={user}
                orderId={orderId}
            />
        )
    },
)
